import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLocation } from '@xing-com/crate-core-router';
import { TextButton } from '@xing-com/crate-lebenslauf-button';
import type { ProgressBarState } from '@xing-com/crate-lebenslauf-components';
import {
  getProgressBarState,
  Jobs,
  ProgressBar,
} from '@xing-com/crate-lebenslauf-components';
import {
  isValidColor,
  isValidTemplate,
} from '@xing-com/crate-lebenslauf-helpers';
import {
  useBasePath,
  useLastDocument,
  useReadVitaReactiveVariable,
  useVitaReactiveVariableActions,
  useVitaTracking,
} from '@xing-com/crate-lebenslauf-hooks';
import {
  Form,
  Preferences,
  Preview,
  Templates,
} from '@xing-com/crate-lebenslauf-xinglets';
import { NoSSR } from '@xing-com/crate-utils-no-ssr';
import { IconArrowDownward, IconGridView } from '@xing-com/icons';

import {
  ColorPickerStyled,
  Container,
  Footer,
  GlobalStyles,
  StyledAnschreibenButton,
  StyledPagination,
  StyledPreviewButton,
  StyledPreviewButtonText,
} from './new.styled';

type LocationState = {
  color?: string;
  template?: string;
};

const extractThemeFromHash = (hash?: string): LocationState => {
  const [template, color] = hash ? hash.substring(1).split('-') : [];
  return { template, color };
};

export const New: React.FC = () => {
  const { trackWithNWT, getTrackingPrefixes } = useVitaTracking({
    pageName: 'editor',
  });
  const basePath = useBasePath();
  const { hash, pathname } = useLocation();
  const { template, color } = extractThemeFromHash(hash);
  const { themeActions } = useVitaReactiveVariableActions();
  const { cv, showAnschreibenBanner } = useReadVitaReactiveVariable();
  const [iconOnlyPreview, setIconOnlyPreview] = useState(false);
  const [isShown, setIsShown] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [pageVisible, setPageVisible] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [progressBarState, setProgressBarState] = useState<ProgressBarState>({
    personalInformation: {
      isFilled: false,
      intersectionRatio: 1,
    },
    professionalExperience: {
      isFilled: false,
      intersectionRatio: 0,
    },
    educationExperience: {
      isFilled: false,
      intersectionRatio: 0,
    },
    skills: {
      isFilled: false,
      intersectionRatio: 0,
    },
    summary: {
      isFilled: false,
      intersectionRatio: 0,
    },
  });
  const togglePreferences = (): void => {
    setShowPreferences(!showPreferences);
  };

  useLastDocument();

  React.useEffect(() => {
    trackWithNWT({
      context: 'cv_editor_form_start',
    });
  }, [trackWithNWT]);

  const toggle = (): void => {
    setIsShown(!isShown);
    trackWithNWT({
      event: 'clicked',
      element: 'change_template',
    });
  };
  const handleSectionVisibilityChange = (
    section: string,
    intersectionRatio: number
  ): void => {
    const newSectionsVisibility = {
      ...progressBarState,
      [section]: {
        isFilled: progressBarState[section].isFilled,
        intersectionRatio,
      },
    };

    setProgressBarState(newSectionsVisibility);
    // transition to the icon-only button - 600px scroll offset is a fitting position for the length of our form
    setIconOnlyPreview(window.scrollY > 600);
  };

  useEffect(() => {
    if (cv) {
      setProgressBarState(getProgressBarState({ cv, progressBarState }));
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [cv]);

  useEffect(() => {
    const uppercaseTemplate = template?.toUpperCase();
    const uppercaseColor = color?.toUpperCase();

    if (isValidTemplate(uppercaseTemplate)) {
      themeActions({ type: 'change-template', value: uppercaseTemplate });
    }

    if (isValidColor(uppercaseColor)) {
      themeActions({ type: 'change-color', value: uppercaseColor });
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [color, template]);

  useEffect(() => window.scrollTo(0, 0), [pathname]);

  const trackingNwtCreateCoverLetter = (): void => {
    trackWithNWT({
      event: 'opened',
      element: 'anschreiben',
    });
  };

  const handleClickOnJobsMatching = (): void => {
    trackWithNWT({
      event: 'clicked',
      element: 'find_jobs',
      sentBy: 'anschreiben',
    });
  };

  return (
    <>
      <GlobalStyles $showAnschreibenBanner={!!showAnschreibenBanner} />

      <ProgressBar progressBarState={progressBarState} />

      <Container $hasBanner={!!showAnschreibenBanner}>
        <NoSSR>
          <ColorPickerStyled />
          <Preview
            pageVisible={pageVisible}
            setTotalPages={setTotalPages}
            onOpenPreview={toggle}
          />
          <Footer>
            <Jobs
              togglePreferences={togglePreferences}
              onClick={handleClickOnJobsMatching}
            />
            <TextButton
              icon={IconGridView}
              onClick={toggle}
              data-qa="lebenslauf-templates-button"
            >
              <FormattedMessage id="CRATE_LEBENSLAUF_CHANGE_TEMPLATE" />
            </TextButton>
            <StyledPagination
              pageVisible={pageVisible}
              totalPages={totalPages}
              setPageVisible={setPageVisible}
            />
          </Footer>
        </NoSSR>
      </Container>

      {isShown ? <Templates isShown={isShown} onToggle={toggle} /> : null}

      <Form onSectionVisibilityChange={handleSectionVisibilityChange} />

      <StyledAnschreibenButton
        variant="proJobs"
        size="large"
        icon={IconArrowDownward}
        onClick={trackingNwtCreateCoverLetter}
        to={`${basePath}/anschreiben?sc_o=${getTrackingPrefixes().cv}_editor_anschreiben_click`}
      >
        <FormattedMessage id="CRATE_VITA_TEMPLATE_COVER_LETTER_TAG" />
      </StyledAnschreibenButton>

      <StyledPreviewButton
        variant="primary"
        onClick={toggle}
        size="medium"
        $iconOnlyPreview={iconOnlyPreview}
      >
        <IconGridView width={24} height={24} />
        <StyledPreviewButtonText $iconOnlyPreview={iconOnlyPreview}>
          <FormattedMessage id="CRATE_LEBENSLAUF_PREVIEW_BUTTON" />
        </StyledPreviewButtonText>
      </StyledPreviewButton>

      <Preferences
        show={showPreferences}
        togglePreferences={togglePreferences}
      />
    </>
  );
};
